import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { PopoverController } from '@ionic/angular';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { ModalPlanUpgradedComponent } from 'src/app/components/modal-plan-upgraded/modal-plan-upgraded.component';
import { UserI } from 'src/app/interfaces/user.interface';
import { ApiSdkService } from 'src/app/services/api-sdk.service';
import { MixpanelService } from 'src/app/services/mixpanel/mixpanel.service';
import { ResultHandlerService } from 'src/app/services/result-handler.service';
import { phoneExtensions } from 'src/assets/international-phone-extensions/int-phone-extensions';

@Component({
  selector: 'app-plan-selected',
  templateUrl: './plan-selected.page.html',
  styleUrls: ['./plan-selected.page.scss'],
})
export class PlanSelectedPage implements OnInit {

  constructor(
    private route: ActivatedRoute,
    private apiSdkService: ApiSdkService,
    public formBuilder: FormBuilder,
    private resultHandlerService: ResultHandlerService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private popoverCtrl: PopoverController,
    private mixpanel: MixpanelService
  ) { }

  pricingCards: any[] = [
    {
      title: 'free',
      id: 'free',
      price: '0',
      features: [
        { id: 'projectSize', text: 'Para probar el programa' },
        { id: 'performance', text: 'Buen rendimiento' },
        { id: 'integrations', text: 'Integraciones limitadas' },
        { id: 'users', text: '2 usuarios incluidos' },
        { id: 'storage', text: '1GB de espacio' },
        { id: 'api', text: 'Uso de la API' },
        { id: 'ai', text: 'Totalum AI' },
        { id: 'tutorials', text: 'Tutoriales interactivos' },
        { id: 'support', text: 'Soporte por e-mail' },
        { id: 'limit', text: 'Límite de 100 elementos de cada tipo (ej. 100 clientes, productos, etc)' }
      ]
    },
    {
      title: 'basic',
      id: 'shared',
      price: '49.95',
      features: [
        { id: 'projectSize', text: 'Para empresas/proyectos pequeños' },
        { id: 'users', text: '5 usuarios incluidos' },
        { id: 'storage', text: '15GB de espacio' },
        { id: 'banking', text: 'Integración bancaria' },
        { id: 'limit', text: 'Límite de 10.000 elementos de cada tipo (ej. 10.000 clientes, productos, etc)' }
      ]
    },
    {
      title: 'professional',
      id: 'profesional',
      price: '149.95',
      features: [
        { id: 'projectSize', text: 'Para empresas/proyectos medianos' },
        { id: 'performance', text: 'Alto rendimiento' },
        { id: 'integrations', text: 'Integraciones ilimitadas' },
        { id: 'users', text: '15 usuarios incluidos' },
        { id: 'storage', text: '50GB de espacio' },
        { id: 'support', text: 'Soporte por e-mail y teléfono' },
        { id: 'limit', text: 'Límite de 150.000 elementos de cada tipo (ej. 150.000 clientes, productos, etc)' }
      ]
    },
    {
      title: 'enterprise',
      id: 'enterprise',
      price: '299.95',
      features: [
        { id: 'projectSize', text: 'Para empresas/proyectos grandes' },
        { id: 'performance', text: 'Alto rendimiento' },
        { id: 'users', text: '+75 usuarios'},
        { id: 'storage', text: '300GB de espacio'},
        { id: 'support', text: 'Soporte prioritario por e-mail y teléfono'},
        { id: 'limit', text: '+500.000 elementos de cada tipo' }
      ]
    }
  ];

  userForm!: FormGroup;
  userData: UserI = null as any;
  userCard: any = null;

  upgradingPlan = false;

  selectedPlan: any;

  planTitle: string = '';

  itAlreadyHasUserData: boolean = false;

    
  tooltipVisible: boolean = false;
  tooltipPosition = { top: '0px', left: '0px', width: '0px' };
  searchTerm: string = '';

  phoneExtensions = phoneExtensions; // Your initial large array of phone extensions

  @ViewChild('tooltip', { static: false }) tooltipRef!: ElementRef;
  @ViewChild('triggerInput', { static: false }) triggerInputRef!: ElementRef;

  getFeaturesForPlan(planTitle: string, pricingCards: any[]): any[] {
    // Find the selected plan and all its predecessors
    const selectedPlanIndex = pricingCards.findIndex(plan => plan.title === planTitle);
    const plansToConsider = pricingCards.slice(0, selectedPlanIndex + 1);
  
    // Merge features from all predecessors, with priority given to the features of the selected plan
    const mergedFeatures = plansToConsider.reduce((accumulatedFeatures, currentPlan) => {
      currentPlan.features.forEach((feature: any) => {
        const existingFeatureIndex = accumulatedFeatures.findIndex((f: any) => f.id === feature.id);
        if (existingFeatureIndex !== -1) {
          accumulatedFeatures[existingFeatureIndex] = feature; // Override with the latest feature
        } else {
          accumulatedFeatures.push(feature); // Add new feature
        }
      });
      return accumulatedFeatures;
    }, []);
  
    return mergedFeatures;
  }

  getPlan(planTitle: string) {
    const planFeatures = this.getFeaturesForPlan(planTitle, this.pricingCards);
    let plan = this.pricingCards.find(p => p.title === planTitle);
    plan.features = planFeatures;
    return plan;
  }
  
  async ngOnInit() {

    this.userForm = this.formBuilder.group({
      email: [{value:'', disabled: true}],
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      phone: ['', [Validators.required, Validators.minLength(9), Validators.maxLength(15), Validators.pattern('[- +()0-9]+')]],
      country: ['', [Validators.required,Validators.minLength(3), Validators.maxLength(100), this.validateCountryName()]],
      city: ['', [Validators.required,Validators.minLength(3), Validators.maxLength(100)]],
      cp: ['', [Validators.required,Validators.minLength(3), Validators.maxLength(20)]],
      address: ['', [Validators.required,Validators.minLength(5), Validators.maxLength(150)]],
      companyName: ['', [Validators.required,Validators.minLength(3), Validators.maxLength(100)]],
      nif: ['', [Validators.required,Validators.minLength(6), Validators.maxLength(20)]]
    });

    this.route.queryParams.subscribe((params: any) => {
      const planTitle = params['plan']; // Assuming the query parameter is named 'plan'
      if (planTitle) {

        this.mixpanel.createTracking({
          type: 'visit_page',
          place: 'Plan selected',
          action: {
            plan: planTitle
          }
        });

        this.planTitle = planTitle;

        this.selectedPlan = this.getPlan(planTitle);
      } else {
        // Handle case where no plan is specified
      }
    });

    
    await this.getUserData();
    await this.getUserStripeCard();

    /*const modal = await this.popoverCtrl.create({
      component: ModalPlanUpgradedComponent,
      cssClass: 'modal-too-much-elements',
      mode: 'ios',
      backdropDismiss: true,
      showBackdrop: true,
    });

    modal.present();*/
    
  }

  get errorControl() {
    return this.userForm.controls;
  }

  async onSubmit() {

    // Trigger form validation for all fields by marking them as touched
    Object.values(this.userForm.controls).forEach(control => {
      control.markAsTouched();
    });

    // Check if the form is valid before proceeding with form submission
    if (this.userForm.valid) {
        await this.apiSdkService.editUserData(this.userForm.value);
        this.resultHandlerService.showToast({message: 'Datos guardados', color: 'success'});
        // if is the first time user is created (name is null), redirect to account page
        this.itAlreadyHasUserData = true;

        this.mixpanel.createTracking({
          type: 'click',
          place: 'Plan selected - Form data fulfilled',
          action: {
            plan: this.planTitle
          }
        });

        /*if(!this.userData?.name) {
          this.router.navigate(['/account']);
          setTimeout(() => {
            window.location.reload();
          }, 100); 
        }*/
    }
  }

 /* async getData(){
    const userResult = await this.apiSdkService.getFullUserData();
    this.user = userResult.success.data;
    const cardsResult = await this.apiSdkService.getUserStripeCard();
    this.userCard = cardsResult.success.data;
    console.log(this.user);
    console.log(this.userCard);
  }*/

  async getData(){
    const userResult = await this.apiSdkService.getFullUserData();
    this.userData = userResult.success.data;
    const cardsResult = await this.apiSdkService.getUserStripeCard();
    this.userCard = cardsResult.success.data;
  }

  async uploadCardFinish() {
    //this.changeCard = false;
    await this.getData();
  }

  async getUserData() {
    const result = await this.apiSdkService.getFullUserData();
    this.userData = result.success.data;

    this.userForm.patchValue(this.userData);
    
    if (this.userData?.name && this.userData?.phone && this.userData?.country && this.userData?.city && this.userData?.cp && this.userData?.address && this.userData?.companyName && this.userData?.nif) {
      this.itAlreadyHasUserData = true;
    }
  }

  async getUserServers() {
    // get user servers from this.user.totalumServers

  }

  async getUserStripeCard() {
    const cardsResult = await this.apiSdkService.getUserStripeCard();
    this.userCard = cardsResult.success.data;
  }

  async onUpgradePlan() {
    this.upgradingPlan = true;
    // todo: pilla el plan 0 pero y si fuera otro? no puedes cambiar el plan si tienes mas de un totalum
    const totalumInstance = this.userData.totalumInstances?.[0];
    const recaptchaToken = await this.recaptchaV3Service.execute('login').toPromise();
    const plan = this.selectedPlan.id;

    this.mixpanel.createTracking({
      type: 'click',
      place: 'Plan selected - Upgrade plan clicked',
      action: {
        currentPlan: totalumInstance?.plan,
        newPlan: this.selectedPlan?.title
      }
    });

    const result = await this.apiSdkService.changeInstancePlan(totalumInstance?.organizationId!, plan, recaptchaToken!);
    
    this.mixpanel.createTracking({
      type: 'click',
      place: 'Plan selected - Plan upgraded',
      action: {
        currentPlan: totalumInstance?.plan,
        newPlan: this.selectedPlan?.title
      }
    });

    this.upgradingPlan = false;

    const modal = await this.popoverCtrl.create({
      component: ModalPlanUpgradedComponent,
      cssClass: 'modal-too-much-elements',
      mode: 'ios',
      backdropDismiss: true,
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      window.location.reload();
    });
  }


  // NEW CODE 

  onExtensionSelectorClick(e: any) {
    e.stopPropagation(); // Prevent the click from immediately triggering the @HostListener
    this.showTooltip(e);
  }

  showTooltip(event: MouseEvent) {
    this.tooltipVisible = true;
    const targetElement = event.target as HTMLElement;

    const rect = (event.target as HTMLElement).getBoundingClientRect();
    this.tooltipPosition.top = rect.bottom + window.scrollY + 'px';
    this.tooltipPosition.left = rect.left + window.scrollX + 'px';

    if (targetElement.id) {
        const elementById = document.getElementById(targetElement.id);
        if (elementById) {
            this.tooltipPosition.width = elementById.getBoundingClientRect().width + 'px';
        }
    }
  }

  onSelectCountry(country: any) {
    this.userForm.controls['country'].setValue(country.name_es);
    this.tooltipVisible = false;
  }

  setSearchTerm(event: any) {
    this.searchTerm = event.target.value;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.tooltipVisible && !this.tooltipRef.nativeElement.contains(event.target) && !this.triggerInputRef.nativeElement.contains(event.target)) {
      this.tooltipVisible = false;
    }
  }

  validateCountryName(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      const valid = this.phoneExtensions.some(ext => ext.name_es === control.value || ext.name_es.toLowerCase() === control.value || ext.name_es.toUpperCase() === control.value);
      return valid ? null : { 'invalidCountryName': { value: control.value } };
    };
  }

}
